"use client";
import { IResource } from "@gettactic/api";
import { useBooleanState } from "@gettactic/helpers";
import { useSvgToPngImage } from "@gettactic/helpers/src/hooks/useSvgToPngImage";
import { MapCanvasValue } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext";
import { MapViewerSwitch } from "@/features/map/components/MapView/MapViewerSwitch";
import {
  MapInteractiveZoneActionOptions,
  MapInteractiveZones,
  ZoneLink
} from "@gettactic/map-builder/src/types";
import React from "react";
import { ReactSVG } from "react-svg";
import { MapLoadingSpinner } from "@/features/map/components/MapView/MapLoadingSpinner";
import { useMap } from "@/features/map/MapProvider";

export interface FloorMapProps {
  floor: IResource;
  isEditing?: boolean;
  onMapLoaded?: (error: Error | null, svg?: SVGSVGElement) => void;
  onMapEditLoaded?: (error: Error | null, svg?: SVGSVGElement) => void;
  style?: React.CSSProperties;
  showLabel?: boolean;
  label?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  renderAsImage?: boolean;
  loadSvgAsImage?: boolean;
  resources?: IResource[];
  /** Native interactive-zone data for map-builder maps. When provided the
   *  viewer paints spot states and handles clicks itself — no clone overlay. */
  interactiveZoneInfo?: MapInteractiveZones;
  /** Hover callback for map-builder spots (viewport coords). */
  highlightResource?: (
    resId: string | null,
    options: MapInteractiveZoneActionOptions
  ) => void;
  /** Handler for zones linked to another office map / external URL. */
  onZoneLinkClick?: (link: ZoneLink) => void;
  /**
   * True inside a small embedded map preview (e.g. a request's location
   * card). The legend portals to document.body and pins itself near the
   * viewport's bottom-right corner regardless of the map's own container
   * size, so it reads as misplaced on top of the page in a small preview —
   * suppressed there the same way it already is while the map editor is open.
   */
  embedded?: boolean;
}

export const FloorMap: React.FC<FloorMapProps> = ({
  floor,
  isEditing = false,
  onMapLoaded,
  onMapEditLoaded,
  style,
  showLabel = false,
  label = "",
  onMouseLeave,
  onMouseEnter,
  renderAsImage,
  loadSvgAsImage = false,
  resources,
  interactiveZoneInfo,
  highlightResource,
  onZoneLinkClick,
  embedded = false
}) => {
  const { forceMapBuilder, isMapEditing } = useMap();
  const svgElRef = React.useRef<SVGSVGElement | null>(null);
  const mapData = floor.map_data
    ? (JSON.parse(floor.map_data) as MapCanvasValue)
    : undefined;

  const isMapLoaded = useBooleanState();

  const { imgSrc, status, error } = useSvgToPngImage({
    svg: svgElRef.current,
    scale: 2,
    enabled: renderAsImage && !!svgElRef.current && isMapLoaded.state,
    debug: true // set to true for verbose console logs
  });

  const isMapBuilder = forceMapBuilder || (!floor.map_url && !!mapData);

  // Store callbacks in refs so the ref callback below never changes identity.
  // onMapLoaded / onMapEditLoaded are inline arrows from the parent and change
  // every render. If they were useCallback deps, the ref callback would get a
  // new identity, and React 19 would detach(null) + re-attach(element),
  // re-triggering setState → re-render → infinite loop.
  const onMapLoadedRef = React.useRef(onMapLoaded);
  const onMapEditLoadedRef = React.useRef(onMapEditLoaded);
  onMapLoadedRef.current = onMapLoaded;
  onMapEditLoadedRef.current = onMapEditLoaded;

  // Stable callback for ReactSVG afterInjection — uses refs to avoid
  // changing identity on every render (which would cause ReactSVG to
  // re-inject and trigger an infinite setState loop in React 19).
  // Signature matches Errback: (error: Error | null, svg?: SVGSVGElement) => void
  const handleSvgReady = React.useCallback(
    (err: Error | null, svg?: SVGSVGElement) => {
      if (err) {
        if (isEditing) onMapEditLoadedRef.current?.(err, svg);
        else onMapLoadedRef.current?.(err, svg);
        return;
      }
      if (!svg) return;
      svgElRef.current = svg;

      if (!renderAsImage) {
        if (isEditing) onMapEditLoadedRef.current?.(null, svg);
        else onMapLoadedRef.current?.(null, svg);
      }

      isMapLoaded.true();
      // When renderAsImage=true, wait for <img onLoad>
    },
    [isEditing, renderAsImage]
  );

  const handleImageLoad = () => {
    if (!svgElRef.current) return;
    if (isEditing) onMapEditLoaded?.(null, svgElRef.current);
    else onMapLoaded?.(null, svgElRef.current);
  };

  const handleSvgImageLoad = () => {
    isMapLoaded.true();
    onMapLoaded?.(null, undefined);
  };

  const mapBuilderRef = React.useCallback(
    (svg: SVGSVGElement | null) => {
      if (!svg) return;
      if (isEditing) {
        onMapEditLoadedRef.current?.(null, svg);
        return;
      }
      onMapLoadedRef.current?.(null, svg);
      isMapLoaded.true();
    },
    [isEditing]
  );

  const commonStyle: React.CSSProperties = {
    display: "block",
    borderRadius: 8,
    cursor: "default",
    outline: "none",
    ...(style || {})
  };

  return (
    <>
      {loadSvgAsImage && floor.map_url ? (
        <img
          src={`${floor.map_url}?t=svg`}
          alt={label || floor.name || "map"}
          style={commonStyle}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onLoad={handleSvgImageLoad}
          onError={handleSvgImageLoad}
          draggable={false}
        />
      ) : imgSrc && renderAsImage ? (
        <img
          src={imgSrc}
          alt={label || floor.name || "map"}
          style={commonStyle}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onLoad={handleImageLoad}
          draggable={false}
        />
      ) : isMapBuilder ? (
        <div
          style={{
            display: "block",
            borderRadius: "8px",
            cursor: "default",
            outline: "none"
          }}
        >
          <div>
            <MapViewerSwitch
              floor={floor || undefined}
              resources={resources || []}
              interactiveZoneInfo={interactiveZoneInfo || {}}
              highlightResource={highlightResource}
              onZoneLinkClick={onZoneLinkClick}
              mapEditPosition={null}
              ref={mapBuilderRef}
              style={style}
              useSvg
              // Suppress the legend overlay while the map editor is open (it
              // portals to document.body, so it would otherwise stay visible
              // on top of the editor) or inside a small embedded preview
              // (same reason it lands in the wrong spot there — see the
              // `embedded` prop doc above).
              hideZoneLegend={isMapEditing || embedded}
            />
          </div>
        </div>
      ) : (
        <div
          style={style}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <ReactSVG
            afterInjection={handleSvgReady}
            src={`${floor.map_url}?t=svg`}
            loading={MapLoadingSpinner}
            key={`${floor.id}-${isEditing ? "edit" : "view"}`}
          />
        </div>
      )}
      {/* LABEL */}
      {isMapLoaded.state && showLabel && (
        <div
          className={"pointer-events-none flex items-center justify-center"}
          style={{
            transform: "rotateX(0deg)",
            transformStyle: "preserve-3d",
            pointerEvents: "none"
          }}
        >
          <div
            className={
              "pointer-events-none mt-2 inline-block rounded-md border-1 border-neutral-400 bg-secondary px-2 py-1 text-center font-medium text-4xl text-neutral-800 shadow-lg"
            }
            style={{
              transform: "rotateX(0deg)",
              transformStyle: "preserve-3d"
            }}
          >
            {label || floor.name || ""}
          </div>
        </div>
      )}
    </>
  );
};
